import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import APModal from 'components/Modal/APModal';
import { Spinner } from 'components/Loading';
import { getUserInfo } from 'containers/App/actions';
import { usePushSiteError } from 'hooks/usePushSiteError';
import { saveStorage, removeStorage, getStorage } from 'utils/cookies';
import { syncProjectsToES, checkSyncStatus } from './api/es-sync';

import './sync-modal.scss';

const SyncModal = ({ userId }) => {
  const dispatch = useDispatch();
  const pushSiteError = usePushSiteError();
  const isSyncing = getStorage('is-syncing-user-data', userId);
  const jobId = getStorage('project-migration-job-id', userId);
  const [isLoading, setIsLoading] = useState(true);
  const [needsMigration, setNeedsMigration] = useState(false);
  const [isDismissed, setIsDismissed] = useState(isSyncing);
  const [isFinished, setIsFinished] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const check = async (_jobId) => {
      const status = await waitForJobCompletion(null, _jobId);
      if (status === 'completed') {
        setIsFinished(true);
        removeStorage('project-migration-job-id');
        removeStorage('is-syncing-user-data');
      }
      if (status === 'failed') {
        setError(true);
        removeStorage('project-migration-job-id');
        removeStorage('is-syncing-user-data');
      }
    };

    const handleLoading = async () => {
      try {
        const { job_id } = await syncProjectsToES(userId);
        setIsLoading(false);
        if (job_id) {
          setNeedsMigration(true);
          saveStorage('project-migration-job-id', userId, job_id);
          check(job_id);
        }
      }
      catch (err) {
        setIsLoading(false);
        setError(true);
      }
    };

    if (!jobId) {
      handleLoading();
    }
    else {
      check(jobId);
    }
  }, []);

  // Once sync has finished, need to re-run fetch.
  useEffect(() => {
    if (isFinished) {
      dispatch(getUserInfo());
    }
  }, [isFinished]);

  const dismiss = () => {
    setIsDismissed(true);
    saveStorage('is-syncing-user-data', userId, true);
  };

  if (!needsMigration) {
    return null;
  }

  if (isLoading)
    return null;

  if (error) {
    pushSiteError('Please try again later or contact support.');
    return;
  }

  if (isDismissed) {
    return null;
  }

  if (isFinished) {
    return (
      <APModal
        open
        name="sync-complete"
        title="Welcome back"
        hasHeader
        onClose={() => setIsDismissed(true)}
        actions={(
          <button
            className="button"
            onClick={() => setIsDismissed(true)}
          >
            OK
          </button>
        )}
      >
        Your account has finished syncing. Click the button below to continue.
      </APModal>
    );
  }

  return (
    <APModal
      open
      name="sync-weddings"
      title="Welcome back"
      hasHeader
      onClose={dismiss}
    >
      <div className="message">
        Looks like you haven't signed in for awhile. Please wait a moment while we
        sync your account.
      </div>
      <div className="spinner">
        <Spinner />
      </div>
    </APModal>
  );
};

const waitForJobCompletion = (userId, jobId) => {
  return new Promise((resolve, reject) => {
    const check = async () => {
      const { status } = await checkSyncStatus(userId, jobId);
      if (status === 'failed') {
        reject(status);
        return;
      }
      if (status === 'completed') {
        resolve(status);
        return;
      }
      setTimeout(check, 2000);
    };

    check();
  });
};

export default SyncModal;
